import React, { useCallback, useEffect, useState } from "react";
import { Copy, Eye, EyeOff, X } from "lucide-react";
import { ApiKey, apiKeysApi, datasourceApi } from "api/models";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { handleAxiosAndCaughtError } from "utils";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "constants/date";

interface EditApiKeyFormProps {
  onClose: () => void;
  onSuccess: () => void;
  apiKey: ApiKey | null;
  workspaceId?: string;
}

const EditApiKeyForm: React.FC<EditApiKeyFormProps> = ({
  onClose,
  onSuccess,
  apiKey,
  workspaceId,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [formData, setFormData] = useState({
    expires_at: "",
    allowed_origins: [] as string[],
    allowed_ips: [] as string[],
  });

  // Track original data for comparison
  const [originalData, setOriginalData] = useState({
    expires_at: "",
    allowed_origins: [] as string[],
    allowed_ips: [] as string[],
  });

  // Fetch permissions on component mount
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!workspaceId) return;

      setLoadingPermissions(true);
      try {
        const permissionsList = await datasourceApi.getPermissions(workspaceId);
        setPermissions(permissionsList);
      } catch (err) {
        handleAxiosAndCaughtError(err);
        // Fallback to default permission if API fails
        setPermissions(["CHAT_WITH_AGENT"]);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [workspaceId]);

  // Validation functions
  const isValidDomain = (domain: string): boolean => {
    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain) && domain.includes(".");
  };

  const isValidIP = (ip: string): boolean => {
    const ipv4Regex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    const cidrRegex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(?:[0-9]|[1-2][0-9]|3[0-2])$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip) || cidrRegex.test(ip);
  };

  const addArrayItem = (
    field: "allowed_origins" | "allowed_ips",
    value: string
  ) => {
    if (value.trim()) {
      let processedValue = value.trim();

      if (field === "allowed_origins") {
        processedValue = processedValue.replace(/^https?:\/\//, "");
        processedValue = processedValue.replace(/\/$/, "");
        processedValue = processedValue.replace(/^www\./, "");

        if (!isValidDomain(processedValue)) {
          toast.error(t("apiKeys.validDomain"));
          return;
        }
      } else if (field === "allowed_ips") {
        if (!isValidIP(processedValue)) {
          toast.error(t("apiKeys.validIp"));
          return;
        }
      }

      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], processedValue],
      }));
    }
  };

  const removeArrayItem = (
    field: "allowed_origins" | "allowed_ips",
    index: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("apiKeys.messages.apiKeyCopied"));
    } catch (err) {
      toast.error(t("apiKeys.messages.copyFailed"));
    }
  };

  const maskApiKey = (key: string) => {
    if (!key) return "";
    return (
      key.substring(0, 8) +
      "•".repeat(key.length - 12) +
      key.substring(key.length - 4)
    );
  };

  const resetForm = useCallback(() => {
    setFormData({
      expires_at: "",
      allowed_origins: [],
      allowed_ips: [],
    });
    setShowApiKey(false);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKey || !workspaceId) return;

    setIsSubmitting(true);
    try {
      const requestData = {
        name: apiKey.name,
        expire_date: formData.expires_at || new Date().toISOString(),
        permissions: apiKey.permissions,
        domains: formData.allowed_origins,
        ip_addresses: formData.allowed_ips,
      };

      await apiKeysApi.update(workspaceId, apiKey.id, requestData);
      toast.success(t("apiKeys.messages.updatedSuccessfully"));
      onSuccess();
      handleClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if there are any changes
  const hasChanges = () => {
    if (!apiKey) return false;

    const currentExpiresAt = formData.expires_at || "";
    const originalExpiresAt = originalData.expires_at || "";

    return (
      currentExpiresAt !== originalExpiresAt ||
      JSON.stringify(formData.allowed_origins.sort()) !==
        JSON.stringify(originalData.allowed_origins.sort()) ||
      JSON.stringify(formData.allowed_ips.sort()) !==
        JSON.stringify(originalData.allowed_ips.sort())
    );
  };

  // Initialize form data when apiKey changes
  useEffect(() => {
    if (apiKey) {
      const expiresAt = apiKey.expire_date
        ? dayjs(apiKey.expire_date).format("YYYY-MM-DDTHH:mm")
        : "";
      const origins = apiKey.domains || [];
      const ips = apiKey.ip_addresses || [];

      setFormData({
        expires_at: expiresAt,
        allowed_origins: origins,
        allowed_ips: ips,
      });

      setOriginalData({
        expires_at: expiresAt,
        allowed_origins: [...origins],
        allowed_ips: [...ips],
      });
    }
  }, [apiKey]);

  if (!apiKey) {
    return <div>{t("apiKeys.noApiKeySelected")}</div>;
  }

  const isExpired = apiKey.expire_date
    ? dayjs().isAfter(dayjs(apiKey.expire_date))
    : false;

  return (
    <form onSubmit={handleSubmit} className="flex min-h-[60vh] flex-col gap-4">
      <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t("apiKeys.editForm.title")}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t("apiKeys.editForm.description")}
        </p>
      </div>

      <div className="space-y-6">
        {/* Read-only fields */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("common.name")}
            </label>
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {apiKey.name || t("apiKeys.unnamedApiKey")}
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("common.status")}
            </label>
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {isExpired ? (
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-300">
                  {t("apiKeys.statusExpired")}
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  {t("apiKeys.statusActive")}
                </span>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("common.createdAt")}
          </label>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {apiKey.created_at
              ? dayjs(apiKey.created_at).format(DATE_TIME_FORMAT)
              : t("apiKeys.notAvailable")}
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("apiKeys.editForm.apiKeyLabel")}
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-md bg-gray-100 px-3 py-2 dark:bg-gray-800">
              <code className="break-all font-mono text-sm">
                {showApiKey ? apiKey.api_key : maskApiKey(apiKey.api_key || "")}
              </code>
            </div>
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="rounded-md border border-gray-300 px-3 py-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              {showApiKey ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={() => copyToClipboard(apiKey.api_key || "")}
              className="rounded-md border border-gray-300 px-3 py-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("apiKeys.editForm.apiKeyHelper")}
          </p>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("apiKeys.permissions")}
          </label>
          {loadingPermissions ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {t("apiKeys.loadingPermissions")}
              </div>
            </div>
          ) : permissions.length === 0 ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {t("apiKeys.noPermissionsAvailable")}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {permissions.map((permission) => (
                <div
                  key={permission}
                  className="flex min-w-[200px] items-center gap-2"
                >
                  <input
                    type="radio"
                    id={`permission-${permission}`}
                    name="permission"
                    value={permission}
                    checked={apiKey.permissions?.includes(permission) || false}
                    disabled
                    className="mt-1 h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <label
                    htmlFor={`permission-${permission}`}
                    className="flex-1 cursor-not-allowed"
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {permission
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {apiKey.permissions?.includes(permission)
                        ? t("apiKeys.editForm.permissionGranted")
                        : t("apiKeys.editForm.permissionNotAssigned")}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t("apiKeys.editForm.permissionsInfo")}
          </p>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("apiKeys.expiresAt")}
          </label>
          <input
            type="datetime-local"
            value={formData.expires_at}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, expires_at: e.target.value }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("apiKeys.editForm.expiresAtHint")}
          </p>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("apiKeys.editForm.allowedOriginsLabel")}
          </label>
          <div className="space-y-2">
            <div className="min-h-[40px] rounded-md border border-gray-300 px-3 py-2 focus-within:ring-2 focus-within:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              <div className="flex flex-wrap items-center gap-2">
                {formData.allowed_origins.map((origin, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-xs text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                  >
                    <span className="max-w-[100px] truncate">{origin}</span>
                    <button
                      type="button"
                      onClick={() => removeArrayItem("allowed_origins", index)}
                      className="ml-1 flex-shrink-0 transition-colors hover:text-purple-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={
                    formData.allowed_origins.length === 0
                      ? t("apiKeys.domainPlaceholder")
                      : ""
                  }
                  className="bg-transparent min-w-[100px] flex-1 border-none text-sm outline-none"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addArrayItem("allowed_origins", e.currentTarget.value);
                      e.currentTarget.value = "";
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      addArrayItem("allowed_origins", e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("apiKeys.domainDescription")}
            </p>
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("apiKeys.allowedIpAddresses")}
          </label>
          <div className="space-y-2">
            <div className="min-h-[40px] rounded-md border border-gray-300 px-3 py-2 focus-within:ring-2 focus-within:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              <div className="flex flex-wrap items-center gap-2">
                {formData.allowed_ips.map((ip, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                  >
                    <span className="max-w-[100px] truncate">{ip}</span>
                    <button
                      type="button"
                      onClick={() => removeArrayItem("allowed_ips", index)}
                      className="ml-1 flex-shrink-0 transition-colors hover:text-blue-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={
                    formData.allowed_ips.length === 0
                      ? t("apiKeys.ipPlaceholder")
                      : ""
                  }
                  className="bg-transparent min-w-[120px] flex-1 border-none text-sm outline-none"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addArrayItem("allowed_ips", e.currentTarget.value);
                      e.currentTarget.value = "";
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      addArrayItem("allowed_ips", e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("apiKeys.ipDescription")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-20 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:px-4"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !hasChanges()}
          className="flex-1 rounded-lg bg-brand-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? t("common.updating") : t("apiKeys.updateApiKey")}
        </button>
      </div>
    </form>
  );
};

export default EditApiKeyForm;
